import {Alert, Autocomplete, Button, Checkbox, CircularProgress, FormControlLabel, Grid2 as Grid, Link, Menu, MenuItem, Table, TableBody, TableCell, TableRow, TextField, Tooltip} from "@mui/material"
import React, {useState} from "react"
import {useNavigate} from 'react-router-dom';
import {Add, Ballot, Close, Delete, Download, Edit, History, Image, Launch, MoreHoriz, MoveDown, MoveUp, Remove, Storage, UploadFile, Warning} from "@mui/icons-material";
import {download} from "../../services/FileService";
import VisuallyHiddenInput from "../common/VisuallyHiddenInput";
import TableHead from "@mui/material/TableHead";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {limit} from "../../util/StringUtils";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";

const fileColumns = [
    {id: 'name', label: 'Fájl Neve'},
    {id: 'description', label: 'Leírás'},
    {id: 'createdAt', label: 'Feltöltve'},
    {id: 'uploadedBy', label: 'Feltöltő'},
];

const iterationColumns = [
    {id: 'serialNumber', label: 'Gyártási ciklus azonosítója'},
    {id: 'amount', label: 'Mennyiség'},
    {id: 'createdAt', label: 'Létrehozva'},
    {id: 'updatedAt', label: 'Módosítva'},
]

const productColumns = [
    {
        id: 'providedData',
        label: 'Importált Név',
        disabled: (objects) => objects.every(o => !o?.providedData || o?.providedData?.length === 0)
    },
    {id: 'product', label: 'Termék', subId: "name", width: "600px"},
    {id: 'amount', label: 'Mennyiség', editable: true},
    {
        id: 'averagePurchasePrice',
        label: 'Átl. Besz. Ár',
        align: "right",
        disabled: (objects, priceView) => !priceView,
        fun: (r) => {
            const arr = r?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
            const avg = arr.length ? arr.reduce((sum, x) => sum + x.purchasePrice, 0) / arr.length : 0;
            return avg.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
        }
    },
    {
        id: '',
        label: 'Árajánlat Mennyiség',
        align: "right",
        disabled: (objects, priceView) => !priceView,
        fun: (r, priceViewAmount) => r.amount * priceViewAmount
    },
    {
        id: '',
        label: 'Összesen',
        align: "right",
        disabled: (objects, priceView) => !priceView,
        fun: (r, priceViewAmount) => {
            const arr = r?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
            const avg = arr.length ? arr.reduce((sum, x) => sum + x.purchasePrice, 0) / arr.length : 0;
            const value = r?.amount * avg * priceViewAmount
            return value.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
        }
    },
    {
        id: 'unitPrice',
        label: 'Egységár (Ft)',
        editable: true,
        align: "right",
        disabled: (objects, priceView) => !priceView
    },
    {
        id: '', label: 'Összesen', disabled: (objects, priceView) => !priceView, fun: (r, priceViewAmount) => {
            const value = r?.amount * r?.unitPrice * priceViewAmount
            return value.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
        }
    },
    {
        id: '', label: 'Különbség', disabled: (objects, priceView) => !priceView, fun: (r, priceViewAmount) => {
            const total = r?.amount * r?.unitPrice

            const arr = r?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
            const avg = arr.length ? arr.reduce((sum, x) => sum + x.purchasePrice, 0) / arr.length : 0;
            const totalAvg = r?.amount * avg
            const diff = (total - totalAvg) * priceViewAmount

            return diff.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
        }
    }
];

const EXCEL_CONFIG_VALUES_LOCAL_STORAGE = "product-project-excel-config-default-values"

export default function ProductProjectEditorTemplate(props) {

    const [open, setOpen] = React.useState(false);
    const [excelFile, setExcelFile] = React.useState(null);
    const [currentItem, setCurrentItem] = React.useState(undefined);

    const [excelUploadDialogOpen, setExcelUploadDialogOpen] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(undefined);
    const [defaultExcelConfigValues, setDefaultExcelConfigValues] = useState(JSON.parse(localStorage.getItem(EXCEL_CONFIG_VALUES_LOCAL_STORAGE)))

    const [downloadInProgress, setDownloadInProgress] = React.useState(false);
    const [selectedFiles, setSelectedFiles] = React.useState([]);
    const [priceView, setPriceView] = React.useState(false);
    const [priceViewAmount, setPriceViewAmount] = React.useState(1);

    const [fileContextAnchor, setFileContextAnchor] = useState(null);
    const fileContextOpen = Boolean(fileContextAnchor);
    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileContextButtonClick = (event, file) => {
        setSelectedFile(file);
        setFileContextAnchor(event.currentTarget);
    }
    const handleFileContextClose = () => {
        setSelectedFile(null);
        setFileContextAnchor(null);
    }

    const handleClickOpen = (item) => {
        setCurrentItem(item)
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleExcelUploadDialogOpen = (excelFile, index) => {
        setExcelFile(excelFile)
        setCurrentIndex(index)
        setDefaultExcelConfigValues(JSON.parse(localStorage.getItem(EXCEL_CONFIG_VALUES_LOCAL_STORAGE)))
        setExcelUploadDialogOpen(true);
    }

    const handleExcelUploadDialogClose = () => {
        setExcelUploadDialogOpen(false);
    }

    const {
        id,
        onChange,
        create,
        saveAsNew,
        update,
        message,
        productProject = {},
        waitingForServer
    } = props

    const navigate = useNavigate();

    function downloadAllFiles() {
        setDownloadInProgress(true);
        download(limit(productProject?.description, 30) || "files.zip", `/cloud-storage-files`, props.sendAlertMessage, () => setDownloadInProgress(false), selectedFiles);
    }

    const onFileCheckBoxChange = (event, file) => {
        if (event?.target?.checked) {
            setSelectedFiles(prev => [...prev, file.id]);
        } else {
            setSelectedFiles(prev => prev.filter(id => id !== file.id));
        }
    }

    const [productContext, setProductContext] = React.useState(null);
    const [productContextAnchor, setProductContextAnchor] = useState(null);
    const productContextOpen = Boolean(productContextAnchor);
    const handleProductContextButtonClick = (event, product, index, j) => {
        setProductContext({product, index, j})
        setProductContextAnchor(event.currentTarget);
    }

    const handleProductContextClose = () => {
        setProductContext(null);
        setProductContextAnchor(null);
    }

    const [taskTypeContext, setTaskTypeContext] = React.useState(null);
    const [taskTypeContextAnchor, setTaskTypeContextAnchor] = useState(null);
    const taskTypeContextOpen = Boolean(taskTypeContextAnchor);
    const handleTaskTypeContextButtonClick = (event, taskType, index) => {
        setTaskTypeContext({taskType, index})
        setTaskTypeContextAnchor(event.currentTarget);
    }

    const handleTaskTypeContextClose = () => {
        setTaskTypeContext(null);
        setTaskTypeContextAnchor(null);
    }

    const allProducts = productProject?.taskTypes?.flatMap(tt => tt.products) || [];

    const visibleColumns = productColumns.filter(pc =>
        typeof pc.disabled !== 'function' || !pc.disabled(allProducts, priceView)
    );

    return (
        <div>
            <Paper component="main" sx={{marginBottom: "10px", backgroundColor: "#1976d2", color: "white"}}>
                <Tooltip title={"Alraktárak"}>
                    <IconButton color="inherit" onClick={() => navigate("/dashboard/substorages")}>
                        <Storage sx={{fontSize: "35px"}}/>
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Termékek Mozgatása"}>
                    <IconButton color="inherit" onClick={() => navigate("/dashboard/storage/move")}>
                        <MoveDown sx={{fontSize: "35px"}}/>
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Raktár Történet"}>
                    <IconButton color="inherit" onClick={() => navigate("/dashboard/storage/logs")}>
                        <History sx={{fontSize: "35px"}}/>
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Feladatkörök"}>
                    <IconButton color="inherit" onClick={() => navigate("/dashboard/task-types")}>
                        <Ballot sx={{fontSize: "35px"}}/>
                    </IconButton>
                </Tooltip>
            </Paper>
            <form id="login" onSubmit={id ? update : create}>
                <Grid container columns={20}>
                    <Grid size={{xs: 20}}>
                        <div className="register-paper">
                            <Grid container columns={20}>
                                <Grid size={{xs: 20}}>
                                    <Grid container columnSpacing={2} rowSpacing={0} columns={30}>
                                        {id != null && productProject?.id == null ?
                                            <Grid size={{xs: 30}}><CircularProgress sx={{margin: "200px"}}/>
                                            </Grid> :
                                            <>
                                                <Grid size={{xs: 8}} sx={{textAlign: "center"}}>
                                                    {props.loadingImage ? <CircularProgress sx={{margin: "100px"}}/> : props.imgUrl ? <img src={props.imgUrl} style={{maxHeight: "320px", maxWidth: "320px"}}/> :
                                                        <div style={{
                                                            backgroundColor: "lightgray",
                                                            height: "320px",
                                                            width: "320px",
                                                            borderRadius: "20px",
                                                            fontSize: "240px",
                                                            paddingTop: "0px",
                                                            color: "white",
                                                            lineHeight: "240px",
                                                            margin: "auto"
                                                        }}>📷️</div>}
                                                </Grid>
                                                <Grid size={{xs: 22}}>
                                                    <Grid container spacing={2} columns={30}>
                                                        {createInputField("id", "Projektszám",productProject?.identifier ? productProject?.identifier : "Projektszám generálva lesz", onChange, false, false, true)}
                                                        {createInputField("name", "Név", productProject?.name, onChange, true)}
                                                        {message &&
                                                            <Grid size={{xs: 30}}>
                                                                <Alert severity="error" variant="standard"
                                                                       style={{color: "red"}}>{message}</Alert>
                                                            </Grid>}
                                                    </Grid>
                                                </Grid>
                                            </>
                                        }

                                        <Grid size={{xs: 2, md: 30}} sx={{height: "50px"}}></Grid>

                                        <Grid size={{xs: 8, md: 6, lg: 4}}>
                                            <Button type="button" fullWidth variant="contained" color="error"
                                                    onClick={() => navigate(-1)}>
                                                Vissza
                                            </Button>
                                        </Grid>

                                        <Grid size={{md: 12, lg: 10}}></Grid>

                                        <Grid size={{md: 4, lg: 4}}>
                                            <FormControlLabel
                                                control={<Checkbox checked={priceView}
                                                                   onChange={(e) => setPriceView(!priceView)}/>}
                                                label="Árajánlat nézet"/>
                                        </Grid>

                                        <Grid size={{xs: 8, md: 6, lg: 4}}>
                                            {!waitingForServer && id &&
                                                <Button type="button" fullWidth variant="contained" color="warning"
                                                        onClick={() => props.saveAsDraft()}>
                                                    Piszkozat mentése
                                                </Button>}
                                        </Grid>

                                        <Grid size={{xs: 8, md: 6, lg: 4}}>
                                            {!waitingForServer && id &&
                                                <Button type="button" fullWidth variant="contained" color="error"
                                                        disabled={!props.isDraft}
                                                        onClick={() => props.deleteDraft()}>
                                                    Piszkozat elvetése
                                                </Button>}
                                        </Grid>

                                        <Grid size={{xs: 8, md: 6, lg: 4}}>
                                            {waitingForServer ? <CircularProgress sx={{margin: "-5px"}}/> :
                                                <Tooltip
                                                    title={props.isDraft ? "Piszkozat még nem került mentésre szerveren, csak ezen az eszközön létezik" : "Termék Projekt mentése"}>
                                                    <Button type="submit" fullWidth variant="contained" color="success"
                                                            sx={{position: "relative"}}>
                                                        {props.isDraft &&
                                                            <Warning sx={{position: 'absolute', left: '10px'}}/>}
                                                        Mentés
                                                    </Button>
                                                </Tooltip>}
                                        </Grid>
                                    </Grid>

                                </Grid>
                                <Grid size={{xs: 20}}>
                                    <Grid container columns={5} sx={{marginTop: "4px"}}>
                                        <Grid size={{xs: 5}}>
                                            <Table sx={{
                                                marginTop: "16px",
                                                tableLayout: props.productProject?.files?.length > 0 ? "fixed" : ""
                                            }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell style={{
                                                            fontWeight: 700,
                                                            background: "lightgray",
                                                            width: "100px"
                                                        }}
                                                                   align='left' padding='none'
                                                                   sx={{paddingLeft: "8px"}}>
                                                            <Tooltip title={"Fájl feltöltése"}>
                                                                <Button variant='contained' color='success'
                                                                        component={'label'}
                                                                        sx={{
                                                                            minWidth: "0px",
                                                                            padding: "0px",
                                                                            marginRight: "10px"
                                                                        }}>
                                                                    <VisuallyHiddenInput type="file" multiple
                                                                                         onChange={props.handleFileChange}/>
                                                                    <Add fontSize="large"/>
                                                                </Button>
                                                            </Tooltip>
                                                            {downloadInProgress ?
                                                                <div style={{
                                                                    display: "inline-block",
                                                                    position: "relative"
                                                                }}>
                                                                    <CircularProgress size={"35px"} sx={{
                                                                        marginTop: "0px",
                                                                        position: "absolute",
                                                                        top: -22
                                                                    }}/>
                                                                </div> :
                                                                <Tooltip title={"Összes letöltése"}>
                                                                    <Button variant='contained' color='warning'
                                                                            component={'label'}
                                                                            onClick={() => downloadAllFiles()}
                                                                            sx={{
                                                                                minWidth: "0px",
                                                                                padding: "0px",
                                                                                marginRight: "0px",
                                                                                paddingTop: "-10px"
                                                                            }}
                                                                            disabled={selectedFiles?.length <= 0}>
                                                                        <Download fontSize="large"/>
                                                                    </Button>
                                                                </Tooltip>
                                                            }
                                                        </TableCell>
                                                        {fileColumns.map((column, index) => (
                                                            <TableCell key={index} align={column.align} style={{
                                                                fontWeight: 700,
                                                                background: "lightgray"
                                                            }}>
                                                                {column.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {props.productProject?.files?.length > 0 ? props.productProject?.files?.map((file, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{width: "50px", padding: "0px"}}>
                                                                <Button
                                                                    onClick={(e) => handleFileContextButtonClick(e, file)}
                                                                    variant="text" sx={{
                                                                    marginLeft: "0px",
                                                                    padding: "5px",
                                                                    minWidth: "0px"
                                                                }}>
                                                                    <MoreHoriz fontSize="large" color="action"/>
                                                                </Button>

                                                                <Menu
                                                                    anchorEl={fileContextAnchor}
                                                                    open={fileContextOpen}
                                                                    onClose={handleFileContextClose}
                                                                    anchorOrigin={{
                                                                        vertical: "bottom",
                                                                        horizontal: "right"
                                                                    }}
                                                                    transformOrigin={{
                                                                        vertical: "top",
                                                                        horizontal: "right"
                                                                    }}
                                                                >
                                                                    <MenuItem onClick={() => {
                                                                        handleFileContextClose()
                                                                        handleClickOpen(selectedFile)
                                                                    }}><Edit sx={{
                                                                        fontSize: "20px",
                                                                        color: "gray",
                                                                        marginRight: "10px"
                                                                    }}/>Módosítás</MenuItem>
                                                                    <MenuItem onClick={() => {
                                                                        handleFileContextClose()
                                                                        props.changeProductImage(id, selectedFile.id)
                                                                    }}><Image sx={{
                                                                        fontSize: "20px",
                                                                        color: "gray",
                                                                        marginRight: "10px"
                                                                    }}/>Beállítás képként</MenuItem>
                                                                    <MenuItem onClick={() => {
                                                                        props.removeDocumentName(selectedFile.id);
                                                                        handleFileContextClose()
                                                                    }}><Delete sx={{
                                                                        fontSize: "20px",
                                                                        color: "gray",
                                                                        marginRight: "10px"
                                                                    }}/>Törlés</MenuItem>
                                                                </Menu>

                                                            </TableCell>
                                                            <TableCell key={"B-" + index}
                                                                       sx={{textAlign: "left", padding: "0px"}}>
                                                                <Link
                                                                    onClick={() => download(file.name, `/cloud-storage-files/${file.id}`, props.sendAlertMessage)}
                                                                    sx={{
                                                                        textDecoration: "none",
                                                                        cursor: "pointer",
                                                                        fontWeight: "bold"
                                                                    }}>{file.name}</Link>
                                                            </TableCell>
                                                            <TableCell sx={{padding: "0px"}}>
                                                                    <span style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        padding: "0px"
                                                                    }}>
                                                                        <span style={{
                                                                            flexGrow: 1,
                                                                            textAlign: 'left'
                                                                        }}>{file.description}</span>
                                                                    </span>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{padding: "0px"}}>{file.createdAt}</TableCell>
                                                            <TableCell
                                                                sx={{padding: "0px"}}>{file.uploadedBy?.name}</TableCell>
                                                        </TableRow>
                                                    )) : <TableRow><TableCell colSpan={20} sx={{
                                                        textAlign: "center",
                                                        fontStyle: "italic"
                                                    }}>Nincs feltöltött fájl</TableCell></TableRow>}
                                                    {props.files?.map((file, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{width: "50px", padding: "0px"}}>
                                                                <Button
                                                                    onClick={() => props.handleRemoveFile(index)}
                                                                    variant="text" color='error' sx={{
                                                                    marginLeft: "10px",
                                                                    padding: "5px",
                                                                    minWidth: "0px"
                                                                }}>
                                                                    <Close/>
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                textAlign: "left",
                                                                padding: "0px 0px 0px 5px"
                                                            }}>
                                                                {file.name} {' ('}{(new Intl.NumberFormat().format((file.size / 1024).toFixed(2))).replace(',', '.')} KB{')'}
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell></TableCell>

                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                        </div>
                    </Grid>
                </Grid>

                {priceView &&
                    <div className="register-paper" style={{padding: "10px", borderRadius: "5px", marginTop: "20px"}}>
                        <Grid container columns={20}>
                            <Grid size={{md: 4}}><TextField variant="outlined" value={priceViewAmount}
                                                            onChange={(e) => setPriceViewAmount(e.target.value)}
                                                            label="Árajánlat Mennyiség"/>
                            </Grid>
                        </Grid>
                    </div>}

                <div className="register-paper" style={{padding: "10px", borderRadius: "5px", marginTop: "20px"}}>
                    <Grid container columns={20} rowSpacing={1} columnSpacing={1}>

                        {Array.from({length: productProject?.taskTypes?.length}, (_, index) => (
                            <React.Fragment key={index}>
                                <Grid size={{xl: 1, lg: 2, md: 2, sm: 3, xs: 5}}>
                                    <Button
                                        onClick={(e) => handleTaskTypeContextButtonClick(e, productProject?.taskTypes[index], index)}
                                        variant="text" sx={{
                                        marginLeft: "0px",
                                        marginTop: "24px",
                                        padding: "0px",
                                        minWidth: "0px"
                                    }}>
                                        <MoreHoriz fontSize="large" color="action" sx={{color: "gray"}}/>
                                    </Button>
                                </Grid>

                                <Grid size={{md: 8, sm: 3, xs: 5}}>
                                    <Autocomplete
                                        value={productProject?.taskTypes[index].taskType || null}
                                        label="taskType"
                                        name="taskType"
                                        disableClearable
                                        onChange={(event, newValue) => props.onTaskTypeChange(index, newValue, "taskType")}
                                        options={props.taskTypes}
                                        getOptionLabel={(taskType) => taskType.name}
                                        style={{marginTop: "16px"}}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Feladat Típus" required/>
                                        )}
                                    />
                                </Grid>

                                <Grid size={{md: 2, sm: 3, xs: 5}}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        label="Időtartam (Óra)"
                                        name="estimatedTime"
                                        type="text"
                                        autoComplete="off"
                                        sx={{marginTop: "16px"}}
                                        onChange={(e) => props.onTaskTypeItemChange(e, index)}
                                        value={productProject?.taskTypes[index].estimatedTime || ""}
                                    />
                                </Grid>

                                <Grid size={{md: 2, sm: 3, xs: 5}} sx={{paddingTop: "16px"}}>
                                    {priceView && (
                                        <>
                                            Belső Óradíj <br/>
                                            {(productProject?.taskTypes[index]?.taskType?.hourlyWage || 0) + " Ft"}
                                        </>
                                    )}
                                </Grid>

                                <Grid size={{md: 2, sm: 3, xs: 5}} sx={{paddingTop: "16px"}}>
                                    {priceView && (
                                        <>
                                            Teljes Belső Óradíj <br/>
                                            {(productProject?.taskTypes[index]?.taskType?.hourlyWage || 0) * priceViewAmount * productProject?.taskTypes[index].estimatedTime + " Ft"}
                                        </>
                                    )}
                                </Grid>

                                <Grid size={{md: 2, sm: 3, xs: 5}} sx={{paddingTop: "16px"}}>
                                    {priceView && (
                                        <>
                                            Külső Óradíj <br/>
                                            {(productProject?.taskTypes[index]?.taskType?.hourlyRate || 0) + " Ft"}
                                        </>
                                    )}
                                </Grid>

                                <Grid size={{md: 2, sm: 3, xs: 5}} sx={{paddingTop: "16px"}}>
                                    {priceView && (
                                        <>
                                            Teljes Külső Óradíj <br/>
                                            {(productProject?.taskTypes[index]?.taskType?.hourlyRate || 0) * priceViewAmount * productProject?.taskTypes[index].estimatedTime + " Ft"}
                                        </>
                                    )}
                                </Grid>

                                <Grid size={{xs: 9}}></Grid>

                                {<TableContainer style={{maxHeight: "calc(100vh - 160px)"}}>
                                    <Table stickyHeader aria-label="sticky table" sx={{tableLayout: "fixed"}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: "30px", background: "lightgray"}}></TableCell>
                                                {visibleColumns.map((column, index) => (
                                                    <TableCell key={index} align={column.align}
                                                               style={{
                                                                   fontWeight: 700,
                                                                   background: "lightgray",
                                                                   padding: "0px 5px",
                                                                   width: column.width ? column.width : "auto"
                                                               }}>
                                                        {column.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Array.from({length: productProject?.taskTypes[index].products?.length}, (_, j) => (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={j}>
                                                    <TableCell sx={{width: "30px"}}>
                                                        <Button
                                                            onClick={(e) => handleProductContextButtonClick(e, productProject?.taskTypes[index].products[j], index, j)}
                                                            variant="text"
                                                            sx={{marginLeft: "0px", padding: "0px", minWidth: "0px"}}>
                                                            <MoreHoriz fontSize="large" color="action"
                                                                       sx={{color: "gray", marginTop: "0px"}}/>
                                                        </Button>
                                                    </TableCell>
                                                    {visibleColumns.map((column, key) => {
                                                        const row = productProject?.taskTypes[index].products[j]
                                                        let value = column.fun ? column.fun(row, priceViewAmount) :
                                                            column.subId != null ? row[column.id]?.[column.subId] : row[column.id]
                                                        return (
                                                            <TableCell key={key} align={column.align} sx={{
                                                                padding: "0px 10px",
                                                                '& input': {
                                                                    fontSize: "0.8rem !important",
                                                                    padding: "0px"
                                                                }
                                                            }}>
                                                                {column.id === 'product' ?
                                                                    <Autocomplete
                                                                        value={productProject?.taskTypes[index].products[j].product}
                                                                        margin="normal"
                                                                        name="productItem"
                                                                        onInputChange={(e) => props.changeProductSearchText(e == null ? "" : e.target.value, index)}
                                                                        onChange={(event, newValue) => props.changeProduct(index, j, newValue, "product")}
                                                                        onBlur={() => props.changeProductSearchText("", index)}
                                                                        options={productProject?.taskTypes[index].productList || []}
                                                                        filterOptions={(options) => options}
                                                                        getOptionLabel={(item) => (item.articleNumber ? item.articleNumber : " - ") + " | " + (item.name)}
                                                                        sx={{marginTop: "0px"}}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params}
                                                                                       required
                                                                                       variant="standard"
                                                                                       error={productProject?.taskTypes[index].products[j].product?.name != null && productProject?.taskTypes[index].products[j].product?.id == null}
                                                                            />

                                                                        )}
                                                                        isOptionEqualToValue={(option, value) => option.id === value.id}

                                                                    /> : column.editable ?
                                                                        <TextField name={column.id}
                                                                                   variant='standard'
                                                                                   value={value}
                                                                                   onChange={(e) => props.onProductItemChange(e, index, j)}
                                                                                   type={column.type || "text"}>
                                                                        </TextField>
                                                                        : (value == null ? "" : value.toLocaleString())}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell sx={{border: "none"}}>
                                                    <Tooltip title="Új termék hozzáadása" placement="left">
                                                        <Button variant="text" sx={{
                                                            marginLeft: "0px",
                                                            padding: "0px",
                                                            minWidth: "0px"
                                                        }} onClick={() => {
                                                            props.addProductRow(index)
                                                        }}>
                                                            <Add fontSize="small" color="action" sx={{
                                                                color: "gray",
                                                                marginTop: "0px",
                                                                marginLeft: "0px"
                                                            }}/>
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            {priceView && <TableRow sx={{backgroundColor: "#efd981"}}>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>{(productProject?.taskTypes[index].products || []).reduce((sum, product) => sum + (product.amount || 0), 0)}</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell
                                                    sx={{textAlign: "right"}}>{(productProject?.taskTypes[index].products || []).reduce((sum, product) => sum + (product.amount || 0), 0) * priceViewAmount}</TableCell>
                                                <TableCell sx={{textAlign: "right"}}>
                                                    {(() => {
                                                        const sumOfAverages = (productProject?.taskTypes[index].products || []).reduce((sum, pr) => {
                                                            const items = pr?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
                                                            const avg = items.length
                                                                ? items.reduce((s, x) => s + x.purchasePrice, 0) * pr.amount / items.length
                                                                : 0;
                                                            return sum + avg;
                                                        }, 0);

                                                        const result = sumOfAverages * priceViewAmount;
                                                        return result.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
                                                    })()}
                                                </TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>
                                                    {((productProject?.taskTypes[index].products || [])
                                                            .reduce((sum, pr) => sum + ((pr?.amount ?? 0) * (pr?.unitPrice ?? 0)), 0) * (priceViewAmount ?? 0)
                                                    ).toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft"}
                                                </TableCell>
                                                <TableCell>
                                                    {((productProject?.taskTypes[index].products || []).reduce((sum, pr) => {
                                                            const amount = pr?.amount ?? 0;
                                                            const unitPrice = pr?.unitPrice ?? 0;
                                                            const total = amount * unitPrice;

                                                            const items = pr?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
                                                            const avgPurchasePrice = items.length
                                                                ? items.reduce((s, x) => s + x.purchasePrice, 0) / items.length
                                                                : 0;
                                                            const totalAvg = amount * avgPurchasePrice;

                                                            return sum + (total - totalAvg) * (priceViewAmount ?? 0);
                                                        }, 0)
                                                    ).toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft"}
                                                </TableCell>
                                            </TableRow>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>}

                                <Grid size={{md: 23, xs: 10}}></Grid>
                                <Grid size={{md: 20, xs: 13}}></Grid>
                                <Grid size={{xs: 20}}>
                                    <div style={{borderTop: "2px solid rgba(0, 0, 0, 0.2)"}}/>
                                </Grid>
                            </React.Fragment>
                        ))}

                        <Grid size={{xs: 20}}></Grid>


                        <Grid size={{xs: 1}} minWidth={"70px"}>
                            <Tooltip title="Új sor hozzáadása">
                                <Button type="button" variant="contained" color="success"
                                        onClick={() => props.addTaskTypeRow()}>
                                    <Add/>
                                </Button>
                            </Tooltip>
                        </Grid>

                        <Grid size={{xs: 1}} minWidth={"70px"}>
                            <Tooltip title="Utolsó sor törlése">
                                <Button type="button" variant="contained" color="error"
                                        onClick={() => props.removeLastTaskTypeRow()}>
                                    <Remove/>
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </div>

                {priceView && <div className="register-paper" style={{
                    padding: "10px",
                    borderRadius: "5px",
                    marginTop: "20px",
                    marginBottom: "20px"
                }}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{width: "150px"}}>Teljes Beszerzési Ár</TableCell>
                                <TableCell>
                                    {(() => {
                                        const totalForAllIndexes = (productProject?.taskTypes || []).reduce((total, taskType) => {
                                            const sumOfAverages = (taskType.products || []).reduce((sum, pr) => {
                                                const items = pr?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
                                                const avg = items.length
                                                    ? (items.reduce((s, x) => s + x.purchasePrice, 0) / items.length) * pr.amount
                                                    : 0;
                                                return sum + avg;
                                            }, 0);

                                            return total + sumOfAverages;
                                        }, 0);
                                        const finalResult = totalForAllIndexes * priceViewAmount;
                                        return finalResult.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + ' Ft';
                                    })()}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Teljes Eladási Ár</TableCell>
                                <TableCell>
                                    {(() => {
                                        const totalForAllIndexes = (productProject?.taskTypes || []).reduce((total, taskType) => {
                                            const sum = (taskType.products || []).reduce((sum, pr) => {
                                                return sum + ((pr?.amount ?? 0) * (pr?.unitPrice ?? 0));
                                            }, 0);
                                            return total + sum;
                                        }, 0);
                                        const finalResult = totalForAllIndexes * (priceViewAmount ?? 0);
                                        return finalResult.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
                                    })()}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Teljes Belső Munkadíj</TableCell>
                                <TableCell>
                                    {(() => {
                                        const total = productProject?.taskTypes?.reduce((sum, item) => {
                                            const hourlyRate = item?.taskType?.hourlyWage || 0;
                                            const estimatedTime = item?.estimatedTime || 0;
                                            return sum + hourlyRate * priceViewAmount * estimatedTime;
                                        }, 0);
                                        return total?.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
                                    })()}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Teljes Külső Munkadíj</TableCell>
                                <TableCell>
                                    {(() => {
                                        const total = productProject?.taskTypes?.reduce((sum, item) => {
                                            const hourlyRate = item?.taskType?.hourlyRate || 0;
                                            const estimatedTime = item?.estimatedTime || 0;
                                            return sum + hourlyRate * priceViewAmount * estimatedTime;
                                        }, 0);
                                        return total?.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
                                    })()}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Különbség</TableCell>
                                <TableCell>
                                    {(() => {
                                        const totalHourlyWage = productProject?.taskTypes?.reduce((sum, item) => {
                                            const hourlyRate = item?.taskType?.hourlyWage || 0;
                                            const estimatedTime = item?.estimatedTime || 0;
                                            return sum + hourlyRate * priceViewAmount * estimatedTime;
                                        }, 0);

                                        const totalHourlyRate = productProject?.taskTypes?.reduce((sum, item) => {
                                            const hourlyRate = item?.taskType?.hourlyRate || 0;
                                            const estimatedTime = item?.estimatedTime || 0;
                                            return sum + hourlyRate * priceViewAmount * estimatedTime;
                                        }, 0);
                                        const totalForAllIndexes = (productProject?.taskTypes || []).reduce((total, taskType) => {
                                            const sum = (taskType.products || []).reduce((sum, pr) => {
                                                const amount = pr?.amount ?? 0;
                                                const unitPrice = pr?.unitPrice ?? 0;
                                                const totalPrice = amount * unitPrice;
                                                const items = pr?.product?.productItems?.filter(x => x.purchasePrice != null) ?? [];
                                                const avgPurchasePrice = items.length
                                                    ? items.reduce((s, x) => s + x.purchasePrice, 0) / items.length
                                                    : 0;
                                                const totalAvg = amount * avgPurchasePrice;


                                                return sum + (totalPrice - totalAvg) * (priceViewAmount ?? 0);
                                            }, 0);
                                            return total + sum + totalHourlyRate - totalHourlyWage;
                                        }, 0);
                                        return totalForAllIndexes?.toLocaleString('hu-HU', {maximumFractionDigits: 0}) + " Ft";
                                    })()}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>}

                <div className="register-paper"
                     style={{padding: "0px", borderRadius: "5px", marginTop: "20px", marginBottom: "20px"}}>
                    <Table
                        sx={{marginTop: "16px", tableLayout: props.productProject?.files?.length > 0 ? "fixed" : ""}}>
                        <TableHead>
                            <TableRow sx={{borderRadius: "15px"}}>
                                <TableCell style={{
                                    fontWeight: 700,
                                    background: "lightgray",
                                    width: "100px",
                                    borderRadius: "5px 0px 0px 0px"
                                }}
                                           align='left' padding='none' sx={{paddingLeft: "8px"}}>
                                    <Tooltip title={"Új Gyártási ciklus"}>
                                        <Button variant='contained' color='success' component={'label'}
                                                onClick={() => props.navigate(`/dashboard/product-projects/${props.id}/iterations/create`)}
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {iterationColumns.map((column, index) => (
                                    <TableCell key={index} align={column.align}
                                               style={{fontWeight: 700, background: "lightgray"}}>
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.productProject?.iterations?.length > 0 ? props.productProject?.iterations?.map((iteration, index) => (
                                <TableRow key={index} hover>
                                    <TableCell sx={{
                                        width: "100%",
                                        padding: "0px",
                                        height: "30px !important",
                                        display: "inline-flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <Button
                                            onClick={() => props.del(iteration.id)}
                                            variant="text" color='error' sx={{
                                            margin: "0px",
                                            marginLeft: "0px",
                                            padding: "0px",
                                            position: "relative",
                                            minWidth: "24px",
                                            top: "0px",
                                            left: "0px"
                                        }}>
                                            <Delete/>
                                        </Button>
                                    </TableCell>
                                    {iterationColumns.map((column, index) => (
                                        <TableCell key={index}
                                                   sx={{
                                                       padding: "0px",
                                                       height: "30px !important",
                                                       cursor: "pointer"
                                                   }}
                                                   onClick={() => props.navigate(`/dashboard/product-projects/${props.id}/iterations/${iteration.id}`)}>{iteration[column.id]}</TableCell>
                                    ))}
                                </TableRow>
                            )) : <TableRow>
                                <TableCell colSpan={5} sx={{textAlign: "center", fontStyle: "italic"}}>Nincs
                                    Gyártási ciklus</TableCell>
                            </TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </form>

            <Dialog
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        component: 'form',
                        onSubmit: (event) => {
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            const formJson = Object.fromEntries((formData).entries());
                            props.updateFileDescription(currentItem.id, formJson)
                            handleClose();
                        },
                    }
                }}
            >
                <DialogTitle sx={{background: "#1976d2", color: "white"}}>
                    Fájl Leírás Módosítása
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        name="description"
                        label="Leírás"
                        fullWidth
                        autoComplete="off"
                        defaultValue={currentItem?.description}
                        variant="standard"
                    />
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained"
                            sx={{marginRight: "auto"}}>Mentés</Button>
                    <Button onClick={handleClose} color="error"
                            variant="outlined">Mégse</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        component: 'form',
                        onSubmit: (event) => {
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            const formJson = Object.fromEntries((formData).entries());
                            props.updateFileDescription(currentItem.id, formJson)
                            handleClose();
                        },
                    }
                }}
            >
                <DialogTitle sx={{background: "#1976d2", color: "white"}}>
                    Fájl Leírás Módosítása
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        name="description"
                        label="Leírás"
                        fullWidth
                        autoComplete="off"
                        defaultValue={currentItem?.description}
                        variant="standard"
                    />
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained"
                            sx={{marginRight: "auto"}}>Mentés</Button>
                    <Button onClick={handleClose} color="error"
                            variant="outlined">Mégse</Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={excelUploadDialogOpen}
                onClose={handleExcelUploadDialogClose}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        const formJson = Object.fromEntries((formData).entries());
                        localStorage.setItem(EXCEL_CONFIG_VALUES_LOCAL_STORAGE, JSON.stringify(formJson))
                        const request =
                            {
                                firstRowIndex: formJson.firstRowIndex === '' ? 0 : formJson.firstRowIndex - 1,
                                lastRowIndex: formJson.lastRowIndex === '' ? 0 : formJson.lastRowIndex - 1,
                                columnIndexes: {
                                    'amount': formJson.amount - 1,
                                    'articleNumber': formJson.articleNumber - 1,
                                    'name': formJson.name - 1,
                                    'manufacturer': formJson.manufacturer - 1
                                }
                            }
                        props.convertExcelToProductList(excelFile, currentIndex, request);
                        handleExcelUploadDialogClose();
                    },
                }}
            >
                <DialogTitle sx={{background: "#1976d2", color: "white"}}>Importálás Excel Fájlból</DialogTitle>
                <DialogContent>
                    <Typography sx={{fontWeight: "bold", marginTop: "10px"}}>
                        {excelFile?.name}
                    </Typography>
                    {createDialogInputField('firstRowIndex', 'Első sor', defaultExcelConfigValues?.firstRowIndex)}
                    {createDialogInputField('lastRowIndex', 'Utolsó sor', defaultExcelConfigValues?.lastRowIndex)}
                    {createDialogInputField('amount', 'Mennyiség Oszlop Index', defaultExcelConfigValues?.amount)}
                    {createDialogInputField('articleNumber', 'Cikkszám Oszlop Index', defaultExcelConfigValues?.articleNumber)}
                    {createDialogInputField('name', 'Termék Név Oszlop Index', defaultExcelConfigValues?.name)}
                    {createDialogInputField('manufacturer', 'Gyártó Oszlop Index', defaultExcelConfigValues?.manufacturer)}
                </DialogContent>
                <DialogActions>
                    <Button type="submit" color="primary" variant="contained"
                            sx={{marginRight: "auto"}}>Feltöltés</Button>
                    <Button onClick={handleExcelUploadDialogClose} color="error"
                            variant="outlined">Mégse</Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={productContextAnchor}
                open={productContextOpen}
                onClose={handleProductContextClose}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
            >
                <MenuItem onClick={() => {
                    navigate("/dashboard/storage/products/" + productContext.product?.product?.id)
                    handleProductContextClose()
                }}>
                    <Launch sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Termék oldala
                </MenuItem>
                <MenuItem onClick={() => {
                    props.moveProductToAnotherTaskType(productContext.index, productContext.j, true)
                    handleProductContextClose()
                }}>
                    <MoveUp sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Mozgatás fel
                </MenuItem>
                <MenuItem onClick={() => {
                    props.moveProductToAnotherTaskType(productContext.index, productContext.j, false)
                    handleProductContextClose()
                }}>
                    <MoveDown sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Mozgatás le
                </MenuItem>
                <MenuItem onClick={() => {
                    props.removeProductRow(productContext.index, productContext.j)
                    handleProductContextClose()
                }}>
                    <Delete sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Törlés
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={taskTypeContextAnchor}
                open={taskTypeContextOpen}
                onClose={handleTaskTypeContextClose}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
            >
                <MenuItem onClick={e => e.stopPropagation()}>
                    <label style={{display: "flex", alignItems: "center", width: "100%", cursor: "pointer"}}>
                        <VisuallyHiddenInput
                            type="file"
                            onChange={(e) => {
                                handleExcelUploadDialogOpen(e.target.files[0], taskTypeContext.index)
                                e.target.value = ""
                                handleTaskTypeContextClose()
                            }}
                        />
                        <UploadFile sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/> Beolvasás excelből
                    </label>
                </MenuItem>

                <MenuItem onClick={() => {
                    props.swapRecords(taskTypeContext.index, taskTypeContext.index - 1)
                    handleTaskTypeContextClose()
                }}>
                    <MoveUp sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Mozgatás fel
                </MenuItem>
                <MenuItem onClick={() => {
                    props.swapRecords(taskTypeContext.index, taskTypeContext.index + 1)
                    handleTaskTypeContextClose()
                }}>
                    <MoveDown sx={{fontSize: "20px", color: "gray", marginRight: "10px"}}/>Mozgatás le
                </MenuItem>
            </Menu>

        </div>
    )
}

const createDialogInputField = (name, label, defaultValue) => {

    return (
        <TextField
            margin="dense"
            name={name}
            label={label}
            fullWidth
            variant="standard"
            defaultValue={defaultValue}
            type={"number"}
            slotProps={{
                htmlInput: {
                    min: 1,
                    step: 1,
                    pattern: "[0-9]*",
                    onInput: (e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    },
                }
            }}
        />)
}

function createInputField(name, label, value, onChange, required = false, autoFocus = false, disabled = false, type = "text", multiline = false, lg = 10) {
    return (<Grid size={{xs: 40, md: 20, lg: lg}}>
        <TextField
            variant="outlined"
            margin="normal"
            required={required}
            fullWidth
            id={name}
            label={label}
            name={name}
            type={type}
            autoFocus={autoFocus}
            autoComplete="off"
            onChange={onChange}
            value={value || ""}
            disabled={disabled}
            multiline={multiline}
            minRows={multiline ? 5 : 1}
        />
    </Grid>)
}